'use strict';
/* ==========================================================================
   levels/video.js, part 1 (the kit) — "Federalists vs Anti-Federalists" (ratify): the drawing kit of the video (levels/video.js).
   Helpers copied from lessons/free-speech/levels/video.js (same cast, same print-shop staging, same caption bubbles,
   same chapter cards) plus this lesson's pieces: the engraving props with code-drawn stand-ins, the side blocks
   (navy F / brick A, Civic.SIDE), the LEDGER (13 rows from Ratify.STATES: name, month/year, a yes/no tally bar), the
   "RATIFIED: n of 9" counter, the tally-mark piles and the numbered working card.

   Every piece is a pure function of the part time (T / t passed in). No number is typed here: rows, votes, months and
   the counter come from core-local/ratify.js; the working values from Bank.eval of the script's formulas.
   Exposed as window.RatifyKit (the first IIFE of levels/video.js; the scenes follow).
   ========================================================================== */
(function () {
  const HANDF = "'Caveat Brush'", TYPE = "'Special Elite'", SLAB = "'Alfa Slab One'", SERIF = "'Abril Fatface'";
  const INK = '#2b2420', PENCIL = 'rgba(43,36,32,0.62)', RED = '#b3261e', GREEN = '#2e5a4f', GOLD_INK = '#7d5f14';
  const KC = () => Civic.C;
  const TITLE_PAL = [['#1f2f55', '#f3ead7'], ['#9e2b25', '#f3ead7'], ['#f3ead7', '#1f2f55'], ['#fffdf7', '#9e2b25'], ['news', '#2b2420'], ['#b08a2e', '#2b2420']];
  const DASH = '–';
  const vote = (y, n) => `${y}${DASH}${n}`;
  const NUMW = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen'];
  const numWord = n => NUMW[n] || String(n);

  /* ---------------- small helpers (as free-speech) ---------------- */
  const fin = x => (x === Infinity ? 1e6 : x === -Infinity ? -1e6 : x);
  const cues = ctx => ({ L: id => fin(ctx.L(id)), E: id => fin(ctx.E(id)), W: (id, w, n = 1) => fin(ctx.W(id, w, n)) });
  const segs = () => Game.content.script.segments;
  const segIndex = seg => segs().indexOf(seg);
  const fx = (name, alt) => (Audio.has(name) ? name : alt);
  function fontOk() { return typeof document === 'undefined' || !document.fonts || document.fonts.status === 'loaded'; }
  const widths = new Map();
  function tw(str, font) {
    const k = font + '|' + str; let w = widths.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) { if (widths.size > 2000) widths.clear(); widths.set(k, w); } }
    return w;
  }
  function wrapLines(str, maxW, size, font = HANDF) {
    const words = String(str).split(/\s+/), out = []; let cur = '';
    for (const w of words) { const nx = cur ? cur + ' ' + w : w; if (cur && tw(nx, `${size}px ${font}`) > maxW) { out.push(cur); cur = w; } else cur = nx; }
    if (cur) out.push(cur);
    return out;
  }
  function fitSize(str, maxW, size, font = HANDF, min = 14) { let s = size; while (s > min && tw(str, `${s}px ${font}`) > maxW) s -= 1; return s; }
  function alpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  function txt(str, x, y, size, o = {}) {
    g.save(); g.font = `${size}px ${o.font || HANDF}`; g.fillStyle = o.color || INK; g.textAlign = o.align || 'center'; g.textBaseline = 'middle';
    if (o.alpha != null) g.globalAlpha *= o.alpha;
    if (o.rot) { g.translate(x, y); g.rotate(o.rot); g.fillText(str, 0, 0); } else g.fillText(str, x, y);
    g.restore();
  }
  function branch(c, tbl) {
    let best = Infinity;
    for (const id in tbl) { const v = tbl[id]; if (v == null) continue; const [w, n] = Array.isArray(v) ? v : [v, 1]; best = Math.min(best, v === '@' ? c.L(id) : c.W(id, w, n)); }
    return best;
  }
  const replyIds = q => q.right.concat(...Object.values(q.wrong || {}));
  const replyStart = (c, q) => Math.min(...replyIds(q).map(id => c.L(id)));
  const every = (q, word) => { const o = {}; replyIds(q).forEach(id => { o[id] = word; }); return o; };
  function track(t, keys, dur = 0.6) {
    let x = keys[0][1], y = keys[0][2], s = keys[0][3] ?? 1;
    for (let i = 1; i < keys.length; i++) {
      const k = keys[i]; if (t <= k[0]) continue;
      const e = E.inOutCubic(P(t, k[0], k[0] + (k[4] ?? dur)));
      x = lerp(x, k[1], e); y = lerp(y, k[2], e); s = lerp(s, k[3] ?? s, e);
    }
    return { x, y, s };
  }
  function at(x, y, s, cx, cy, fn, rot = 0) {
    if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s); g.translate(-cx, -cy); try { fn(); } finally { g.restore(); }
  }
  const K = { lastDock: null };
  const dock = (key, lines) => { if (UI.docked()) { Dock.text(key, lines, { cls: 'note' }); K.lastDock = key; } };

  /* ---------------- layers: static art baked once ---------------- */
  const LY = new Map();
  function layer(key, x, y, w, h, draw, o = {}) {
    const res = +Math.min(3, Math.max(0.3, DS * (o.zoom || 1))).toFixed(3);
    let e = LY.get(key);
    if (!e || e.dead || e.res !== res) {
      if (e && !e.dead) OFF.drop(e);
      LY.delete(key);
      const pw = Math.ceil(w * res), ph = Math.ceil(h * res);
      if (!FX.sprites || typeof _bake !== 'function' || !fontOk() || !OFF.room(pw * ph * 4)) { draw(); return false; }
      const cv = _bake(pw, ph, cx => cx.setTransform(res, 0, 0, res, -x * res, -y * res), draw);
      e = OFF.add(cv, { res, x, y, w, h });
      const me = e; e.drop = () => { if (LY.get(key) === me) LY.delete(key); };
      LY.set(key, e);
    }
    e.frame = OFF.frame;
    g.drawImage(e.c, x, y, e.c.width / res, e.c.height / res);
    return true;
  }

  /* ---------------- backdrops (baked) ---------------- */
  function bgShop() {
    layer('rt_shop', 0, 0, 1920, 1080, () => {
      const C = KC();
      g.fillStyle = paperTile(C.kraft, 23, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
      for (let r = 0; r < 3; r++) for (let k = 0; k < 9; k++) {
        const x = 150 + k * 205, y = 250 + r * 150;
        paperRect(x, y, 186, 128, '#8a6a47', { seed: 200 + r * 9 + k, depth: 0.5, boil: false });
        paperRect(x, y, 160, 102, '#6e5238', { seed: 230 + r * 9 + k, depth: 0, boil: false });
        paperRect(x, y - 30, 40, 12, '#c9a25a', { seed: 260 + r * 9 + k, depth: 0, boil: false });
      }
      paperRect(960, 960, 2060, 260, '#5a4632', { seed: 290, torn: true, depth: 1.2, boil: false });
      for (let i = 0; i < 8; i++) ink([[0, 880 + i * 22], [1920, 884 + i * 22]], { w: 1.6, color: 'rgba(30,20,10,0.25)', seed: 291 + i, passes: 1, step: 120 });
      texScrap('tex_news', 1760, 120, 320, 200, { rot: 0.07, seed: 299, depth: 0.6, tint: C.parchment, tintAlpha: 0.5 });
      texScrap('tex_news', 170, 110, 300, 180, { rot: -0.06, seed: 298, depth: 0.6, tint: C.parchment, tintAlpha: 0.5 });
    }, { zoom: 0.75 });
  }
  function bgParch(key = 'rt_parch', seed = 360) {
    layer(key, 0, 0, 1920, 1080, () => {
      const C = KC();
      g.fillStyle = paperTile(C.parchment, 7); g.fillRect(0, 0, 1920, 1080);
      texScrap('tex_news', 160, 150, 360, 260, { rot: -0.08, seed, depth: 0.6, tint: C.parchment, tintAlpha: 0.6 });
      texScrap('tex_news', 1780, 980, 380, 240, { rot: 0.06, seed: seed + 1, depth: 0.6, tint: C.parchment, tintAlpha: 0.6 });
      Civic.bunting(-20, 980, 4, { sag: 40, n: 11, seed: 8, sway: 0 }); Civic.bunting(940, 1940, 4, { sag: 40, n: 11, seed: 9, sway: 0 });
    }, { zoom: 0.75 });
  }
  function bgNews() {
    layer('rt_news', 0, 0, 1920, 1080, () => {
      const C = KC();
      g.fillStyle = paperTile(C.parchment, 7); g.fillRect(0, 0, 1920, 1080);
      [[180, 200, -0.08], [1740, 220, 0.06], [220, 860, 0.05], [1720, 880, -0.05], [960, 90, 0.02]].forEach(([x, y, r], i) =>
        texScrap('tex_news', x, y, 380, 260, { rot: r, seed: 400 + i, depth: 0.7, tint: C.parchment, tintAlpha: 0.45 }));
      paperRect(960, 1010, 2060, 200, '#8a6a47', { seed: 410, torn: true, depth: 1.2, boil: false });
    }, { zoom: 0.75 });
  }
  /* s4: the clerk's desk — dark green baize, a wood rail */
  function bgDesk() {
    layer('rt_desk', 0, 0, 1920, 1080, () => {
      const C = KC();
      g.fillStyle = paperTile('#3f5a4c', 11); g.fillRect(0, 0, 1920, 1080);
      for (let i = 0; i < 14; i++) ink([[0, 40 + i * 76], [1920, 46 + i * 76]], { w: 1.4, color: 'rgba(0,0,0,0.12)', seed: 440 + i, passes: 1, step: 160 });
      paperRect(960, 1020, 2060, 160, '#5a4632', { seed: 460, torn: true, depth: 1.2, boil: false });
      texScrap('tex_news', 1700, 860, 360, 220, { rot: 0.05, seed: 461, depth: 0.6, tint: C.parchment, tintAlpha: 0.5 });
    }, { zoom: 0.75 });
  }

  /* ---------------- paper bits ---------------- */
  function strike(x0, y0, x1, y1, prog, seed = 1, color = RED, w = 9) { ink([[x0, y0], [(x0 + x1) / 2 + 4, (y0 + y1) / 2 - 3], [x1, y1]], { w, color, seed, prog, passes: 2, step: 10 }); }
  function check(x, y, s, prog, seed = 1, color = KC().navy) { ink([[x - 34 * s, y - 2 * s], [x - 8 * s, y + 26 * s], [x + 40 * s, y - 38 * s]], { w: 11 * s, color, seed, prog, passes: 2, step: 8 }); }
  function cross(x, y, s, prog, seed = 1, color = RED) {
    ink([[x - 26 * s, y - 26 * s], [x + 26 * s, y + 26 * s]], { w: 10 * s, color, seed, prog: Math.min(1, prog * 2), passes: 2, step: 8 });
    ink([[x + 26 * s, y - 26 * s], [x - 26 * s, y + 26 * s]], { w: 10 * s, color, seed: seed + 1, prog: Math.max(0, prog * 2 - 1), passes: 2, step: 8 });
  }
  function tag(str, x, y, size, s = 1, o = {}) {
    if (s <= 0.001) return;
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? -0.04); g.scale(s, s);
    const font = `${size}px ${o.font || HANDF}`, w = Math.round(tw(str, font) + size * 0.9), h = Math.round(size * 1.4);
    paperRect(0, 0, w, h, o.color || KC().cream, { seed: o.seed || 41, depth: o.depth ?? 1.0, outline: true });
    g.font = font; g.fillStyle = o.ink || INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(str, 0, size * 0.05);
    g.restore();
  }
  function sticky(lines, x, y, t0, o = {}) {
    const s = pop(T, t0, 0.35); if (s <= 0) return;
    const w = o.w || 280, size = o.size || 34, seed = o.seed || 610, h = Math.round(44 + lines.length * size * 1.15);
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? -0.05); g.scale(s, s);
    paperRect(0, 0, w, h, o.color || '#f2d479', { seed, depth: 1.2 });
    lines.forEach((l, i) => hand(l, 0, -h / 2 + 22 + size * 0.62 + i * size * 1.15, fitSize(l, w - 30, size), (o.times && o.times[i] != null ? o.times[i] : t0 + 0.1 + i * 0.12), { color: o.ink || INK, seed: seed + i + 1, dur: 0.3 }));
    if (o.strike != null) strike(-w / 2 + 16, h / 2 - 18, w / 2 - 16, -h / 2 + 18, P(T, o.strike, o.strike + 0.35), seed + 9);
    tape(0, -h / 2 + 2, 96, 0.05, undefined, seed + 20);
    g.restore();
  }
  function stampMark(text, x, y, s, o = {}) {
    if (s <= 0.001) return;
    const size = o.size || 72, color = o.color || RED, font = `${size}px ${TYPE}`;
    const w = Math.round(tw(text, font) + size * 0.9), h = Math.round(size * 1.5);
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.12); g.scale(s, s); g.globalAlpha *= o.alpha ?? 0.9;
    g.strokeStyle = color; g.fillStyle = color; g.lineJoin = 'round';
    g.lineWidth = size * 0.1; pathPts(rectPts(w, h, 40 + text.length, true, 4)); g.stroke();
    g.lineWidth = size * 0.035; pathPts(rectPts(w - Math.round(size * 0.26), h - Math.round(size * 0.26), 41 + text.length, true, 3)); g.stroke();
    g.font = font; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(text, 0, size * 0.07);
    g.restore();
  }
  const stampS = (t, t0) => (t < t0 ? 0 : E.outBack(P(t, t0, t0 + 0.25), 1.6));
  /* a parchment scroll unrolled to u (0..1) from its top; content() draws inside it (clipped) */
  function scroll(x, y, w, h, u, content, seed = 11) {
    if (u <= 0.001) return;
    const top = y - h / 2, vis = h * u;
    g.save(); g.beginPath(); g.rect(x - w / 2 - 40, top - 30, w + 80, vis + 30); g.clip();
    Civic.parchment(x, y, w, h, { seed, depth: 1.3 });
    if (content) content();
    g.restore();
    paperRect(x, top, w + 44, 30, '#d8c08e', { seed: seed + 1, depth: 0.9, outline: true, boil: false });
    paperRect(x, top + vis, w + 44, 30, '#d8c08e', { seed: seed + 2, depth: 0.9, outline: true, boil: false });
  }
  /* a quote strip: a parchment band with a side-coloured edge, the quote written on, the source in small type */
  function quoteStrip(x, y, w, quote, source, t0, o = {}) {
    const s = pop(T, t0, 0.4, 1.6); if (s <= 0) return 0;
    const size = o.size || 40, lines = wrapLines('"' + quote + '"', w - 110, size, o.font || HANDF);
    const h = Math.round(56 + lines.length * size * 1.18 + 34);
    const color = o.color || KC().brick;
    g.save(); g.translate(x, y); g.rotate(o.rot || 0); g.scale(s, s);
    paperRect(0, 0, w, h, '#fbf3df', { seed: o.seed || 620, depth: 1.4 });
    paperRect(-w / 2 + 16, 0, 16, h - 18, color, { seed: (o.seed || 620) + 1, depth: 0 });
    tape(0, -h / 2 + 2, 110, -0.04, undefined, (o.seed || 620) + 2);
    lines.forEach((l, i) => hand(l, -w / 2 + 56, -h / 2 + 36 + size * 0.6 + i * size * 1.18, size, (o.write ?? t0 + 0.3) + i * 0.35, { align: 'left', color: INK, seed: (o.seed || 620) + 3 + i, dur: 0.45, font: o.font || HANDF }));
    txt(source, w / 2 - 30, h / 2 - 26, 24, { font: TYPE, color: PENCIL, align: 'right' });
    g.restore();
    return h;
  }

  /* ======================================================================
     Staging: puppets, captions, chapter cards (as free-speech)
     ====================================================================== */
  const POS = {
    two: { rex: { x: 250, y: 1088, h: 540, dip: 0 }, judge: { x: 1665, y: 1088, h: 610, dip: 0 } },
    inset: { rex: { x: 124, y: 1098, h: 300, dip: 56 }, judge: { x: 1796, y: 1098, h: 330, dip: 56 } },
  };
  const win = (t, a, b, ramp = 0.35) => Math.min(E.inOutSine(P(t, a, a + ramp)), 1 - E.inOutSine(P(t, b, b + ramp)));
  function stage(t, part, keys) {
    const out = {};
    ['rex', 'judge'].forEach(who => {
      const v = Object.assign({}, POS[keys[0][1]][who]);
      for (let i = 1; i < keys.length; i++) {
        const k = keys[i]; if (t <= k[0]) continue;
        const e = E.inOutCubic(P(t, k[0], k[0] + 0.7)), p = POS[k[1]][who];
        ['x', 'y', 'h', 'dip'].forEach(f => { v[f] = lerp(v[f], p[f], e); });
      }
      const talk = part.lines.filter(l => l.who === who).reduce((a, l) => Math.max(a, win(t, l.start - 0.35, l.end + 0.15, 0.3)), 0);
      v.y += v.dip * (1 - talk);
      out[who] = v;
    });
    return out;
  }
  const REX_POINT = new Set(['s1_01', 's1_02', 's1_04', 's2_04', 's3_03', 's4_03', 's5_04']);
  const JUDGE_POINT = new Set(['s1_03', 's1_05', 's1_06', 's2_01', 's2_03', 's3_01', 's3_04', 's4_01', 's4_02', 's4_06', 's5_02', 's5_05']);
  function cast(t, ctx, st, o = {}) {
    const cap = ctx.caption, id = cap ? cap.id : '', who = cap ? cap.who : '';
    const rp = who === 'rex' && REX_POINT.has(id) ? 'point' : null;
    const jp = who === 'judge' && (JUDGE_POINT.has(id) || ctx.kind === 'reply') ? 'point' : null;
    Puppet.draw('rex', st.rex.x, st.rex.y, st.rex.h, { env: ctx.speaking('rex'), facing: 1, t, pose: rp });
    if (o.front) o.front();
    Puppet.draw('judge', st.judge.x, st.judge.y, st.judge.h, { env: ctx.speaking('judge'), facing: -1, t, pose: jp });
    bubble(ctx, st);
  }
  const BS = () => (Player.captionSize ? Player.captionSize() : 40);
  const pageCache = new Map();
  function pages(l, w) {
    const size = BS(), key = l.id + '|' + w + '|' + l.tokens.length + '|' + size + '|' + l.dur.toFixed(2);
    if (pageCache.has(key)) return pageCache.get(key);
    const toks = l.tokens, n = toks.length, maxW = w - 56;
    g.save(); g.font = `${size}px ${HANDF}`;
    const sp = g.measureText(' ').width, ws = toks.map(k => g.measureText(k.w).width);
    g.restore();
    const rowsOf = (a, b) => { let r = 1, rw = 0; for (let i = a; i < b; i++) { if (rw > 0 && rw + sp + ws[i] > maxW) { r++; rw = 0; } rw += ws[i] + sp; } return r; };
    const out = []; let a = 0;
    while (a < n) {
      let b = a + 1; while (b < n && rowsOf(a, b + 1) <= 2) b++;
      if (b < n) {
        const lo = a + Math.max(1, Math.floor((b - a) * 0.4));
        let cut_ = -1;
        for (const re of [/[.!?]["')]?$/, /[,;:]$/]) { for (let j = b; j >= lo; j--) if (re.test(toks[j - 1].w)) { cut_ = j; break; } if (cut_ > 0) break; }
        if (cut_ > 0) b = cut_;
      }
      out.push([a, b]); a = b;
    }
    const res = out.map(([a_, b_], k) => {
      const t0 = k === 0 ? 0 : Math.max(0, toks[a_].s - 0.15);
      return { t0, text: toks.slice(a_, b_).map(x => x.w).join(' '), rows: rowsOf(a_, b_), tokens: toks.slice(a_, b_).map(x => ({ w: x.w, s: x.s - t0, e: x.e - t0 })) };
    });
    if (pageCache.size > 300) pageCache.clear();
    if (fontOk()) pageCache.set(key, res);
    return res;
  }
  function fit(l, w0, maxW) {
    const BLH = BS() * 1.18;
    for (let w = w0; w <= maxW; w += 40) { const pg = pages(l, w); if (pg.length === 1) return { w, pages: pg, h: pg[0].rows * BLH + 40 }; }
    const pg = pages(l, w0);
    return { w: w0, pages: pg, h: Math.max(...pg.map(x => x.rows)) * BLH + 40 };
  }
  const pageAt = (pg, lt) => { let k = 0; for (let i = 1; i < pg.length; i++) if (lt >= pg[i].t0) k = i; return pg[k]; };
  function bubble(ctx, st) {
    const l = ctx.caption; if (!l) return;
    const p = st[l.who]; if (!p) return;
    const rex = l.who === 'rex', dir = rex ? 1 : -1, side = rex ? 'left' : 'right', k = BS() / 40;
    const tall = p.h > 420;
    const f = fit(l, Math.round(700 * k), Math.round(860 * k));
    const w = f.w, bh = f.h;
    let cx, cy;
    if (tall) { cx = rex ? p.x - 60 + w / 2 : p.x + 60 - w / 2; cy = 84 + bh / 2; }
    else { cx = p.x + dir * (0.2 * p.h + w / 2 + 16); cy = Math.min(876, 968 - bh / 2); }
    cx = Math.max(w / 2 + 16, Math.min(1904 - w / 2, cx));
    cy = Math.max(bh / 2 + 16, cy);
    const cap = { x: cx, y: cy, side, w };
    if (f.pages.length > 1) { const pg = pageAt(f.pages, ctx.t - l.start); Object.assign(cap, { text: pg.text, tokens: pg.tokens, t0: pg.t0 }); }
    ctx.captionAt = cap;
  }

  const CARD = { hold: 1.1, tear: 0.7, lead: 2.2 };
  const cardW = new Map();
  function drawCard(i) {
    const seg = segs()[i]; if (!seg) return;
    const n = i + 1, C = KC();
    g.fillStyle = paperTile(C.kraft, 23, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
    paperRect(960, 548, 1780, 960, C.parchment, { seed: 900 + n, depth: 1.6, boil: false });
    Civic.bunting(140, 1780, 76, { sag: 40, n: 13, seed: 5 + n, sway: 0 });
    txt('CHAPTER', 960, 300, 52, { font: TYPE, color: C.navy });
    Civic.seal(960, 410, 80, { text: String(n), seed: 910 + n });
    const title = String(seg.chapter || seg.id).toUpperCase();
    let w0 = cardW.get(title);
    if (w0 == null) { w0 = ransom(title, -5000, -5000, 100, 1e9, { seed: 950 + n, palette: TITLE_PAL }); if (fontOk()) cardW.set(title, w0); }
    const size = Math.min(124, (1480 / Math.max(1, w0)) * 100);
    ransom(title, 960, 680, size, -1e6, { seed: 950 + n, palette: TITLE_PAL });
    const sub = '~ ' + String((Game.lesson && Game.lesson.title) || '').toLowerCase() + ' ~';
    hand(sub, 960, 880, fitSize(sub, 1500, 44), -1e6, { dur: 0.01, color: 'rgba(31,47,85,0.7)', seed: 960 + n });
  }
  function chapterSfx(part) { return part.kind === 'lines' ? [{ name: 'tear', t: CARD.hold, gain: 0.5 }] : []; }

  /* ======================================================================
     The new engraving props (DESIGN.md §2): the assets agent's cut-out IMG[kind] when it is loaded, else a clean
     code-drawn engraving-style stand-in (paper pieces + ink hatching; drawn at a canonical 100-px size and scaled,
     so the shapes are cached once).
     kinds: broadside | quill_pen | ballot_box | newspaper_1787 | tally_board | bell
     ====================================================================== */
  const _pts = {};
  const pts = (key, make) => _pts[key] || (_pts[key] = Object.assign(make(), { k: 'rt:' + key }));
  function hatch(x0, y0, x1, y1, n, seed, color = 'rgba(43,36,32,0.35)', w = 1.6) {
    for (let i = 0; i < n; i++) { const u = (i + 0.5) / n; ink([[lerp(x0, x1, u), y0], [lerp(x0, x1, u) + (x1 - x0) * 0.06, y1]], { w, color, seed: seed + i, passes: 1, step: 30, amp: 0.4 }); }
  }
  function prop(kind, x, y, h, { s = 1, rot = 0, seed = 31, ax = 0.5, ay = 0.5, flip = false, swing = 0 } = {}) {
    if (s <= 0.001) return;
    if (IMG[kind]) { cut(kind, x, y, h, { ax, ay, s, rot: rot + swing, seed, flip, depth: 1 }); return; }
    const C = KC();
    g.save(); g.translate(x + (0.5 - ax) * h * s, y + (0.5 - ay) * h * s); g.rotate(rot); g.scale(s * (flip ? -1 : 1) * h / 100, s * h / 100);
    if (kind === 'bell') {
      g.translate(0, -44); g.rotate(swing); g.translate(0, 44);
      paperRect(0, -46, 12, 14, '#5a4632', { seed, depth: 0.5 });
      paperPiece(pts('bell', () => { const p = []; for (let i = 0; i <= 12; i++) { const u = i / 12; p.push([-14 - 26 * u * u, -40 + 80 * u]); } p.push([-44, 44], [44, 44]); for (let i = 12; i >= 0; i--) { const u = i / 12; p.push([14 + 26 * u * u, -40 + 80 * u]); } return p; }), 0, 0, '#b08a2e', { seed: seed + 1, depth: 1 });
      hatch(-30, -20, -10, 36, 5, seed + 2, 'rgba(60,40,10,0.45)');
      paperRect(0, 42, 96, 8, '#7d5f14', { seed: seed + 3, depth: 0.3 });
      paperCircle(0, 54, 8, '#5a4632', { seed: seed + 4, depth: 0.5 });
      ink([[12, -12], [20, 26]], { w: 2, color: 'rgba(255,245,210,0.6)', seed: seed + 5, passes: 1 });
    } else if (kind === 'quill_pen') {
      paperPiece(pts('quill', () => [[-6, 48], [4, 48], [26, -20], [20, -48], [6, -36], [-10, 0]]), 0, 0, '#f3ead7', { seed, depth: 0.9 });
      ink([[-1, 48], [22, -44]], { w: 2.2, color: INK, seed: seed + 1, passes: 1 });
      for (let i = 0; i < 7; i++) ink([[3 + i * 3, 30 - i * 11], [12 + i * 3, 26 - i * 11]], { w: 1.2, color: 'rgba(43,36,32,0.45)', seed: seed + 2 + i, passes: 1 });
      paperPiece(pts('nib', () => [[-8, 46], [-1, 58], [2, 46]]), 0, 0, INK, { seed: seed + 9, depth: 0.2 });
    } else if (kind === 'ballot_box') {
      paperRect(0, 10, 80, 64, '#8a6a47', { seed, depth: 1 });
      paperRect(0, -26, 90, 14, '#6e5238', { seed: seed + 1, depth: 0.7 });
      paperRect(0, -26, 36, 4, INK, { seed: seed + 2, depth: 0, boil: false });
      paperRect(6, -40, 26, 26, PAL.white, { seed: seed + 3, depth: 0.5, rot: 0.1 });
      hatch(-36, -18, 30, 40, 7, seed + 4, 'rgba(43,36,32,0.28)');
    } else if (kind === 'newspaper_1787') {
      paperRect(0, 0, 76, 100, '#f1e6cc', { seed, depth: 1, tex: 'tex_news' });
      paperRect(0, -38, 64, 9, INK, { seed: seed + 1, depth: 0, boil: false });
      ink([[-32, -28], [32, -28]], { w: 1.4, color: INK, seed: seed + 2, passes: 1 });
      for (let k = 0; k < 9; k++) for (const cx of [-1, 1]) ink([[cx * 16 - 13, -18 + k * 7], [cx * 16 + 13, -18 + k * 7]], { w: 1.3, color: 'rgba(43,36,32,0.45)', seed: seed + 3 + k * 2 + (cx > 0), passes: 1, step: 20, amp: 0.3 });
    } else if (kind === 'tally_board') {
      paperRect(0, 0, 100, 70, '#6e5238', { seed, depth: 1 });
      paperRect(0, 0, 88, 58, '#2f3a33', { seed: seed + 1, depth: 0, boil: false });
      for (let k = 0; k < 3; k++) { for (let i = 0; i < 4; i++) ink([[-34 + k * 26 + i * 4, -12], [-34 + k * 26 + i * 4, 10]], { w: 1.6, color: 'rgba(240,236,220,0.85)', seed: seed + 2 + k * 5 + i, passes: 1 }); ink([[-37 + k * 26, 6], [-19 + k * 26, -8]], { w: 1.6, color: 'rgba(240,236,220,0.85)', seed: seed + 20 + k, passes: 1 }); }
    } else { // broadside
      paperRect(0, 0, 72, 100, '#f3ead7', { seed, depth: 1 });
      paperRect(0, -36, 58, 12, INK, { seed: seed + 1, depth: 0, boil: false });
      for (let k = 0; k < 8; k++) ink([[-26, -18 + k * 7], [26, -18 + k * 7]], { w: 1.4, color: 'rgba(43,36,32,0.45)', seed: seed + 2 + k, passes: 1, step: 20, amp: 0.3 });
      paperCircle(22, 38, 7, C.wax, { seed: seed + 11, depth: 0.5 });
    }
    g.restore();
  }

  /* ---------------- the two sides' woodtype blocks (Civic.SIDE) ---------------- */
  /* a side card: the big woodtype letter on its block + the label + a line; flip (0..1) turns it over to `back` */
  function sideCard(side, x, y, s, o = {}) {
    if (s <= 0.001) return;
    const S = Civic.SIDE[side], C = KC(), w = o.w || 480, h = o.h || 200;
    const fl = o.flip || 0, sx = Math.abs(Math.cos(fl * Math.PI)), back = fl > 0.5;
    g.save(); g.translate(x, y); g.rotate(o.rot || 0); g.scale(s * Math.max(0.02, sx), s);
    paperRect(0, 0, w, h, back ? '#fbf3df' : C.cream, { seed: (o.seed || 700) + (back ? 5 : 0), depth: 1.3 });
    paperRect(0, -h / 2 + 12, w - 8, 20, S.color, { seed: (o.seed || 700) + 1, depth: 0 });
    if (!back) {
      paperRect(-w / 2 + 84, 12, 120, 132, S.color, { seed: (o.seed || 700) + 2, depth: 1 });
      txt(S.letter, -w / 2 + 84, 18, 104, { font: SLAB, color: S.ink });
      const lx = -w / 2 + 164, lw = w - 190;
      txt(S.label, lx, -22, fitSize(S.label, lw, 40, SLAB), { font: SLAB, color: S.color, align: 'left' });
      if (o.line) hand(o.line, lx, 44, fitSize(o.line, lw, 40), o.t0 ?? -1e6, { align: 'left', color: INK, seed: (o.seed || 700) + 3, dur: 0.4 });
    } else {
      const bl = o.back || [], size = o.backSize || 32;
      txt(S.label + ':', 0, -h / 2 + 52, 26, { font: SLAB, color: S.color });
      bl.forEach((l, i) => txt(l, 0, -h / 2 + 96 + i * size * 1.2, fitSize(l, w - 40, size), { color: INK }));
    }
    g.restore();
  }
  /* a small side badge: the woodtype letter block alone (stamps F / A on a statement) */
  function sideBadge(side, x, y, size, s = 1, rot = -0.08, seed = 720) {
    if (s <= 0.001) return;
    const S = Civic.SIDE[side];
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperRect(0, 0, size, Math.round(size * 1.08), S.color, { seed, depth: 1 });
    txt(S.letter, 0, size * 0.06, Math.round(size * 0.8), { font: SLAB, color: S.ink });
    g.restore();
  }

  /* ======================================================================
     THE LEDGER (DESIGN.md §2): 13 rows from Ratify.STATES. A row: its order, the state's name, the month/year
     (Ratify shown), a tally bar (navy = yes share, brick = no share, a tick at half) and the vote. Everything read
     from the row object; the scene decides only WHEN each row reveals.
     ====================================================================== */
  const LED = { col: { n: 24, name: 70, month: 360, bar0: 520, bar1: 900, vote: 930 } };
  /* o: {name: t0 ink (else pencil), month: t0, bar: t0 (fill), sway: share override (0..1) or null, hi: 0..1 highlight} */
  function ledgerRow(st, x, y, w, rh, o = {}) {
    const C = KC(), c = LED.col, size = Math.round(rh * 0.62), L = x;
    if (o.hi > 0) { g.save(); g.globalAlpha *= 0.28 * o.hi; g.fillStyle = C.gold; g.fillRect(L, y - rh / 2 + 2, w, rh - 4); g.restore(); }
    const inked = T >= (o.name ?? 1e6);
    txt(String(st.order), L + c.n, y, Math.round(size * 0.8), { font: TYPE, color: inked ? C.brick : 'rgba(43,36,32,0.3)' });
    txt(st.name, L + c.name, y + 1, fitSize(st.name, c.month - c.name - 16, size, SLAB), { font: SLAB, color: inked ? INK : 'rgba(43,36,32,0.25)', align: 'left' });
    if (T >= (o.month ?? 1e6)) hand(o.monthText || st.shown, L + c.month, y, Math.round(size * 0.95), o.month, { font: TYPE, align: 'left', color: INK, seed: 800 + st.order, dur: 0.25 });
    const b0 = L + c.bar0, b1 = L + Math.min(c.bar1, w - 110), bw = b1 - b0, bh = Math.round(rh * 0.5);
    g.save(); g.strokeStyle = 'rgba(43,36,32,0.35)'; g.lineWidth = 2; g.strokeRect(b0, y - bh / 2, bw, bh); g.restore();
    if (T >= (o.bar ?? 1e6) || o.sway != null) {
      const grow = o.sway != null ? 1 : E.outCubic(P(T, o.bar, o.bar + 0.45));
      const share = o.sway != null ? o.sway : st.yes / (st.yes + st.no);
      g.save(); g.fillStyle = C.navy; g.fillRect(b0, y - bh / 2, bw * share * grow, bh);
      g.fillStyle = C.brick; g.fillRect(b0 + bw * share, y - bh / 2, bw * (1 - share) * grow, bh); g.restore();
      if (o.sway == null && grow > 0.9) txt(vote(st.yes, st.no), L + Math.min(c.vote, w - 100), y + 1, Math.round(size * 0.9), { font: SLAB, color: INK, align: 'left', alpha: P(T, o.bar + 0.3, o.bar + 0.5) });
    }
    ink([[b0 + bw / 2, y - bh / 2 - 5], [b0 + bw / 2, y + bh / 2 + 5]], { w: 2, color: 'rgba(43,36,32,0.6)', seed: 830 + st.order, passes: 1 });
    return { b0, b1, bh, voteX: L + Math.min(c.vote, w - 100) };
  }
  /* the whole ledger page: rows = Ratify.STATES, rowOpt(state) → the row's options; returns the rows' y */
  function ledger(x, y, w, h, rowOpt, o = {}) {
    const C = KC(), top = y - h / 2, head = 96, rh = (h - head - 20) / Ratify.STATES.length;
    Civic.parchment(x, y, w, h, { seed: o.seed || 840, depth: 1.4 });
    tape(x - w / 2 + 90, top + 4, 120, -0.08, undefined, 841); tape(x + w / 2 - 90, top + 4, 120, 0.08, undefined, 842);
    txt('THE LEDGER', x - w / 2 + 50, top + 42, 38, { font: SLAB, color: C.navy, align: 'left' });
    txt('ratifying conventions', x - w / 2 + 330, top + 46, 26, { font: TYPE, color: PENCIL, align: 'left' });
    const bx = x - w / 2 + 40 + LED.col.bar0, bw = Math.min(LED.col.bar1, w - 150) - LED.col.bar0;
    txt('YES', bx, top + 80, 22, { font: SLAB, color: C.navy, align: 'left' });
    txt('NO', bx + bw, top + 80, 22, { font: SLAB, color: C.brick, align: 'right' });
    ink([[x - w / 2 + 30, top + head], [x + w / 2 - 30, top + head]], { w: 2.5, color: INK, seed: 843, passes: 1, step: 80 });
    const ys = [];
    Ratify.STATES.forEach((st, i) => {
      const yy = top + head + 10 + rh * (i + 0.5); ys.push(yy);
      if (i % 2) { g.save(); g.fillStyle = 'rgba(120,90,40,0.07)'; g.fillRect(x - w / 2 + 30, yy - rh / 2, w - 60, rh); g.restore(); }
      ledgerRow(st, x - w / 2 + 40, yy, w - 80, rh, rowOpt(st));
    });
    return { ys, rh, left: x - w / 2 + 40, bx, bw };
  }
  /* the counter card: "RATIFIED:" n "of 9 needed" (n from the scene: Ratify rows revealed) */
  function counter(x, y, n, s, o = {}) {
    if (s <= 0.001) return;
    const C = KC(), need = Ratify.article7, done = n >= need;
    g.save(); g.translate(x, y); g.rotate(-0.02); g.scale(s, s);
    paperRect(0, 0, 400, 250, done ? '#fbf3df' : C.cream, { seed: 850, depth: 1.4 });
    paperRect(0, -100, 392, 40, C.navy, { seed: 851, depth: 0 });
    txt('RATIFIED', 0, -98, 30, { font: SLAB, color: C.cream });
    txt(String(n), -70, 10, n > 9 ? 92 : 118, { font: SLAB, color: done ? C.navy : INK });
    const over = n > need;
    txt('of ' + (over ? Ratify.count() : need), 60, -12, 46, { font: SLAB, color: INK, align: 'left' });
    txt(over ? `(${need} needed)` : 'needed', 60, 36, 28, { font: TYPE, color: PENCIL, align: 'left' });
    if (o.pulse) inkCircle(-70, 10, 70, { w: 6, color: RED, seed: 852, prog: o.pulse });
    g.restore();
  }

  /* ---------------- tally piles: vertical strokes in rows of 10 (a gap after each 5) ---------------- */
  const TAL = { dx: 17, gap: 14, dy: 58, len: 44 };
  const tallyPos = (x, y, i) => { const r = Math.floor(i / 10), k = i % 10; return [x + k * TAL.dx + (k >= 5 ? TAL.gap : 0), y + r * TAL.dy]; };
  function tallyMark(px, py, color, seed) { ink([[px, py - TAL.len / 2], [px + 2, py + TAL.len / 2]], { w: 6, color, seed, passes: 1, step: 20 }); }

  /* ---------------- the working card (numbered steps; values = Bank.eval of the script's formulas) ---------------- */
  function workingOf(q) {
    const W = q.working; if (!W) return null;
    return W.steps.map((st, i) => {
      const v = Bank.eval(st.formula, W.given);
      return { n: i + 1, text: st.text, value: Bank.format ? Bank.format(v, st.format || 'int') : String(v), v };
    });
  }
  function workCard(q, x, y, w, times, answerT, o = {}) {
    const steps = workingOf(q); if (!steps) return null;
    const s = pop(T, times.cardIn, 0.35, 1.6); if (s <= 0) return steps;
    const rowH = 76, h = 110 + steps.length * rowH + 70;
    g.save(); g.translate(x, y); g.rotate(0.01); g.scale(s, s);
    paperRect(0, 0, w, h, '#fffdf7', { seed: 870, depth: 1.4 });
    for (let k = 0; k < steps.length + 2; k++) ink([[-w / 2 + 20, -h / 2 + 96 + k * rowH], [w / 2 - 20, -h / 2 + 96 + k * rowH]], { w: 1.5, color: 'rgba(31,47,85,0.2)', seed: 871 + k, passes: 1, step: 80 });
    tape(0, -h / 2 + 2, 120, 0.03, undefined, 876);
    txt('THE WORKING', -w / 2 + 34, -h / 2 + 50, 32, { font: SLAB, color: KC().navy, align: 'left' });
    if (o.head) txt(o.head, w / 2 - 30, -h / 2 + 52, 26, { font: TYPE, color: PENCIL, align: 'right' });
    steps.forEach((st, i) => {
      const t0 = times.steps[i], yy = -h / 2 + 96 + i * rowH + rowH / 2;
      if (T < t0) return;
      const line = st.text + ' ' + st.value, size = fitSize(line, w - 120, 36);
      hand(`${st.n}.`, -w / 2 + 40, yy, 30, t0, { font: SLAB, color: KC().brick, seed: 880 + i, dur: 0.15 });
      hand(line, -w / 2 + 84, yy, size, t0 + 0.1, { align: 'left', color: INK, seed: 884 + i, dur: Math.min(0.9, 0.2 + line.length * 0.02) });
    });
    const last = times.steps[steps.length - 1];
    if (T > last + 0.8) {
      const yy = -h / 2 + 96 + steps.length * rowH + 34;
      hand(o.answer || '', -w / 2 + 40, yy, 38, answerT, { font: SLAB, align: 'left', color: GREEN, seed: 890, dur: 0.3 });
    }
    g.restore();
    return steps;
  }

  window.RatifyKit = {
    HANDF, TYPE, SLAB, SERIF, INK, PENCIL, RED, GREEN, GOLD_INK, KC, vote, numWord,
    fin, cues, segs, segIndex, fx, fontOk, tw, wrapLines, fitSize, alpha, txt, branch, replyIds, replyStart, every, track, at, dock, K,
    layer, LY, bgShop, bgParch, bgNews, bgDesk,
    strike, check, cross, tag, sticky, stampMark, stampS, scroll, quoteStrip,
    POS, stage, cast, CARD, drawCard, chapterSfx,
    prop, hatch, sideCard, sideBadge, ledgerRow, ledger, counter, TAL, tallyPos, tallyMark, workingOf, workCard,
  };
})();

/* ==========================================================================
   levels/video.js — step 1, RATIFY IT?: the interactive video (shared/engine/v2/core/player.js), print-shop collage,
   one scene per script segment (content/script.json s1–s5; each segment's "visual" is the storyboard). Modelled on
   lessons/free-speech/levels/video.js (same cast, same staging, same caption bubbles); the drawing kit (helpers,
   props with stand-ins, the ledger, the counter, tally piles, the working card) is the first IIFE above (RatifyKit).

   Every scene is a PURE FUNCTION of the part time t: beats are keyed to the dialogue through c.L / c.E / c.W (±Infinity
   of other parts → ±1e6), so the choreography follows the text timing now and the recorded clips later with no code
   change. A reply cue is min() over the question's reply ids (branch()).

   Honesty (DESIGN.md §4): every vote, month/year, margin, flip, count and quote on screen comes from
   core-local/ratify.js: Ratify.STATES (the ledger rows, facts 2.11–2.23), ratifiedBy / article7 / ninth() (the
   counter and the bell), NC (NOT YET 1788, facts 2.7), margin / afterFlip / flip (New York), unanimousStates(),
   recommendedStates(), PAPERS (85, Publius, New York; facts 3.1), bor (17 → 12 → 10, Dec 1791, the 27th in 1992;
   facts 5.4–5.7), VIEWS (the verified quotes: f-angels 6.3, a-mason 6.6, a-king 6.8, f-why-declare 6.4). The q4
   working values are Bank.eval of the script's formulas. Local storyboard text, each with its facts row: Article VII's
   words (2.1), the Articles' weaknesses (1.2), the FLDOE glossary gloss (1.10). Rex's student-council constitution is
   story (invented).
   Staging: Rex left, the Judge right, full size ("two") or bottom-corner insets ("inset") when the boards need the
   screen (boards inside y 60…790 then). Portrait: the player docks the captions; the key text is also written into the
   dock (Dock.text). QA / debug: window.RatifyVideo (BODY, TEXT, lastDock, counter, steps).
   ========================================================================== */
(function () {
  const S = Player.scenes;
  const RK = window.RatifyKit;
  const { HANDF, TYPE, SLAB, SERIF, INK, PENCIL, RED, GREEN, KC, vote, numWord, cues, segIndex, fx, tw, wrapLines, fitSize, alpha, txt,
    branch, replyStart, every, track, at, dock, strike, check, cross, tag, sticky, stampMark, stampS, scroll, quoteStrip,
    stage, cast, CARD, drawCard, chapterSfx, prop, sideCard, sideBadge, ledgerRow, ledger, counter, TAL, tallyPos, tallyMark, workCard } = RK;
  const R = () => window.Ratify;
  const V = id => R().VIEWS[id];

  /* ---------------- storyboard text (facts-ratify.md rows) ---------------- */
  const TEXT = {
    art7: '...the Conventions of nine States, shall be sufficient...',                          // 2.1 (verbatim fragment)
    weak: ['no power to tax', "can't enforce its laws", 'no national courts'],                 // 1.2
    antiBack: ['feared a strong national', 'government + no written', 'protection of rights'],  // 1.10 (kid words)
    fedLine: 'ratify it', antiLine: 'not like this',
  };
  const RV = { counter: null, steps: null, rows: null };

  /* ======================================================================
     Rex's broadside: the Student Council Constitution (story), reused in s5 with the STUDENT RIGHTS section
     ====================================================================== */
  const BR = { w: 540, h: 680 };
  function broadside(x, y, s, t, o = {}) {
    if (s <= 0.001) return;
    const C = KC(), w = BR.w, h = BR.h, top = -h / 2, L = -w / 2 + 44;
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.025); g.scale(s, s);
    paperRect(0, 0, w, h, '#f6eedb', { seed: 1100, depth: 1.5, tex: 'tex_news' });
    tape(0, top + 2, 120, 0.04, undefined, 1101);
    Civic.woodtype('STUDENT COUNCIL', 0, top + 56, 30, { color: C.navy, ink: C.cream, seed: 1102, stagger: 0 });
    txt('CONSTITUTION', 0, top + 122, 54, { font: SERIF, color: INK });
    ink([[L, top + 160], [-L, top + 160]], { w: 3, color: INK, seed: 1103, passes: 1, step: 60 });
    ink([[L, top + 166], [-L, top + 166]], { w: 1.4, color: INK, seed: 1104, passes: 1, step: 60 });
    txt('ARTICLE I.  THE PRESIDENT', L, top + 200, 24, { font: SLAB, color: C.brick, align: 'left' });
    const a = 'The President (Rex) may make,', b1 = 'enforce ', b2 = 'and judge', b3 = ' all rules.';
    txt(a, L, top + 238, 24, { font: TYPE, align: 'left' });
    txt(b1 + b2 + b3, L, top + 270, 24, { font: TYPE, align: 'left' });
    if (o.strikeT != null && t > o.strikeT) {
      const f = `24px ${TYPE}`, x0 = L + tw(b1, f), x1 = x0 + tw(b2, f);
      strike(x0 - 4, top + 272, x1 + 4, top + 266, P(t, o.strikeT, o.strikeT + 0.35), 1105, RED, 5);
    }
    txt('ARTICLE VII.  RATIFYING', L, top + 318, 24, { font: SLAB, color: C.brick, align: 'left' });
    txt(`In effect when ${R().article7} of the ${R().count()}`, L, top + 354, 24, { font: TYPE, align: 'left' });
    txt('homerooms ratify it.', L, top + 386, 24, { font: TYPE, align: 'left' });
    // s5: the new printed section
    if (o.rightsT != null && t > o.rightsT) {
      Civic.woodtype('STUDENT RIGHTS', 0, top + 446, 28, { color: C.brick, ink: C.cream, t0: o.rightsT, seed: 1106 });
      if (o.lineT != null) ["1. The president can't judge", '    his own case.'].forEach((l, i) => hand(l, L, top + 498 + i * 40, 34, o.lineT + i * 0.5, { align: 'left', color: C.navy, seed: 1107 + i, dur: 0.5 }));
    }
    const sy = top + h - 70;
    hand('signed:', L, sy, 26, -1e6, { align: 'left', dur: 0.01, color: PENCIL, seed: 1110 });
    hand('Rex Delgado, President', L + 90, sy, 34, -1e6, { align: 'left', dur: 0.01, color: C.brick, seed: 1111 });
    g.restore();
  }
  /* the chalk tally board: 13 homerooms, nine needed (R().count(), R().article7) */
  const TB = { w: 460, h: 420 };
  function tallyBoard(x, y, s, t, tm) {
    if (s <= 0.001) return;
    const n = R().count(), need = R().article7, chalk = 'rgba(244,240,226,0.92)';
    g.save(); g.translate(x, y); g.rotate(0.02); g.scale(s, s);
    paperRect(0, 0, TB.w, TB.h, '#6e5238', { seed: 1120, depth: 1.4 });
    paperRect(0, 0, TB.w - 36, TB.h - 36, '#2f3a33', { seed: 1121, depth: 0, boil: false });
    txt(`${n} HOMEROOMS`, 0, -TB.h / 2 + 54, 38, { color: chalk });
    for (let i = 0; i < n; i++) {
      const r = Math.floor(i / 5), k = i % 5, cols = r < 2 ? 5 : n - 10;
      const bx = (k - (cols - 1) / 2) * 76, by = -TB.h / 2 + 124 + r * 80;
      const ps = pop(t, tm.rooms + i * 0.05, 0.3);
      if (ps <= 0) continue;
      g.save(); g.translate(bx, by); g.scale(ps, ps);
      g.strokeStyle = chalk; g.lineWidth = 3; g.strokeRect(-28, -28, 56, 56);
      txt(String(i + 1), 0, 2, 28, { color: chalk });
      g.restore();
      if (i < need && t > tm.need + i * 0.08) ink([[bx - 26, by + 34], [bx + 26, by + 34]], { w: 4, color: '#f2d479', seed: 1130 + i, prog: P(t, tm.need + i * 0.08, tm.need + i * 0.08 + 0.15), passes: 1 });
    }
    if (t > tm.need) hand('NINE NEEDED TO RATIFY', 0, TB.h / 2 - 50, 34, tm.need + 0.6, { color: '#f2d479', seed: 1140, dur: 0.4 });
    g.restore();
  }

  const BODY = {};

  /* ---- s1 "Nine homerooms": the broadside, the tally board, Article VII, the two sides, the flip ---- */
  BODY.s1 = function (t, part, ctx, c) {
    const C = KC(), q = part.seg.question, rs = replyStart(c, q);
    RK.bgShop();
    Civic.bunting(-20, 980, 4, { sag: 46, n: 11, seed: 2 }); Civic.bunting(940, 1940, 4, { sag: 46, n: 11, seed: 3 });
    const tm = {
      con: c.W('s1_01', 'constitution'), rooms: c.W('s1_02', 'nine'), need: c.W('s1_02', 'ratify'), against: c.W('s1_02', 'against'),
      ins: c.L('s1_03') - 0.3, art: c.W('s1_03', 'Article'), states: c.W('s1_03', 'states'), country: c.W('s1_04', 'country'),
      fed: c.L('s1_05'), anti: c.W('s1_05', 'AntiFederalists'), flip: branch(c, every(q, 'feared')),
      list: c.W('s1_06', 'list'), fix: c.W('s1_06', 'fix'),
    };
    // Rex's broadside (s1_01) and the homeroom board (s1_02); both step aside for Article VII (s1_03)
    const bp = track(t, [[0, 760, 560, 1], [tm.ins, 330, 380, 0.52, 0.7]]);
    broadside(bp.x, bp.y, bp.s * pop(t, tm.con, 0.4, 1.6), t);
    const tp = track(t, [[0, 1250, 520, 1], [tm.ins, 1590, 330, 0.6, 0.7]]);
    tallyBoard(tp.x, tp.y, tp.s * pop(t, tm.rooms - 0.2, 0.4, 1.6), t, tm);
    if (t > tm.rooms) prop('ballot_box', tp.x + 170 * tp.s, tp.y + 270 * tp.s, 150 * tp.s, { s: pop(t, tm.rooms + 0.4, 0.35), seed: 1150, rot: 0.04 });
    // "anyone who votes no is against the school" (s1_02) / "against the country" (s1_04): Rex's sticky, struck in the reply
    if (t > tm.against) alpha(1 - P(t, tm.ins, tm.ins + 0.3) + P(t, tm.country, tm.country + 0.3), () => {
      const p = t < tm.country ? [1130, 880] : [960, 470];
      sticky(t < tm.country ? ['vote NO =', 'against the school?'] : ['NO =', 'against the country?'], p[0], p[1], t < tm.country ? tm.against : tm.country,
        { w: 320, size: 34, color: '#f4c6c0', seed: 1160, rot: 0.05, strike: t > rs ? rs + 0.3 : null });
    });
    // Article VII (s1_03), facts 2.1
    const as = pop(t, tm.art, 0.4, 1.6);
    if (as > 0) at(960, 260, as, 960, 260, () => {
      Civic.parchment(960, 260, 780, 290, { seed: 1170, depth: 1.4 });
      Civic.woodtype('ARTICLE VII', 960, 170, 40, { color: C.navy, ink: C.cream, t0: tm.art + 0.1, seed: 1171 });
      hand(TEXT.art7, 960, 250, fitSize(TEXT.art7, 700, 38), tm.art + 0.4, { color: INK, seed: 1172, dur: 0.8 });
      txt('the U.S. Constitution, 1787', 960, 310, 26, { font: TYPE, color: PENCIL });
      if (t > tm.states) {
        const fs = fitSize(TEXT.art7, 700, 38), f = `${fs}px ${HANDF}`, pre = TEXT.art7.slice(0, TEXT.art7.indexOf('nine')), w9 = tw('nine States', f);
        const cx = 960 - tw(TEXT.art7, f) / 2 + tw(pre, f) + w9 / 2;
        inkCircle(cx, 250, w9 / 2 + 14, { sy: 0.42, w: 5, color: RED, seed: 1173, prog: P(t, tm.states, tm.states + 0.4) });
      }
    });
    if (t > tm.art && t < tm.anti) dock('art7', ['Article VII:', `"${TEXT.art7}"`, `Nine of the ${R().count()} states had to say yes.`]);
    // the two sides (s1_05); the brick card flips to the FLDOE definition in the reply (facts 1.10)
    const fl = t > tm.flip ? E.inOutCubic(P(t, tm.flip, tm.flip + 0.6)) : 0;
    sideCard('federalist', 680, 620, pop(t, tm.fed, 0.4, 1.8), { line: TEXT.fedLine, t0: tm.fed + 0.3, seed: 1180, rot: -0.02 });
    sideCard('anti', 1240, 620, pop(t, tm.anti, 0.4, 1.8), { line: TEXT.antiLine, t0: tm.anti + 0.3, seed: 1190, rot: 0.02, flip: fl, back: TEXT.antiBack, backSize: 34 });
    if (t > tm.anti && fl < 0.5) dock('sides', ['Federalists: ratify it.', 'Anti-Federalists: not like this.']);
    if (fl >= 0.5) dock('antiDef', ['Anti-Federalists (FLDOE glossary):', 'they feared a strong national government and a lack of protection for individual rights.']);
    // after: a written list of rights (s1_06); "fix it first, then we're in"
    if (t > tm.list) Civic.prop('scroll_rights', 1600, 610, 170, { s: pop(t, tm.list, 0.35), rot: 0.2, seed: 1195 });
    if (t > tm.fix) tag("fix it first, then we're in", 1240, 470, 34, pop(t, tm.fix, 0.35), { color: '#f2d479', ink: C.brick, seed: 1196, rot: -0.03 });
    const st = stage(t, part, [[0, 'two'], [tm.ins, 'inset']]);
    cast(t, ctx, st);
  };
  S.s1 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 1.4, after: 1.8 }, gaps: { s1_03: 0.6, s1_05: 0.5 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part);
      out.push({ name: 'paper', t: c.W('s1_01', 'constitution'), gain: 0.4 }, { name: 'pop', t: c.W('s1_02', 'nine'), gain: 0.3 });
      out.push({ name: 'scribble', t: c.W('s1_02', 'ratify'), gain: 0.25 }, { name: 'pop', t: c.W('s1_02', 'against'), gain: 0.3 });
      out.push({ name: fx('unroll', 'paper'), t: c.W('s1_03', 'Article'), gain: 0.45 }, { name: 'scribble', t: c.W('s1_03', 'states'), gain: 0.25 });
      out.push({ name: 'pop', t: c.W('s1_04', 'country'), gain: 0.3 }, { name: 'thunk', t: c.L('s1_05'), gain: 0.35 }, { name: 'thunk', t: c.W('s1_05', 'AntiFederalists'), gain: 0.35 });
      out.push({ name: 'scribble', t: replyStart(c, q) + 0.3, gain: 0.3 }, { name: 'whoosh', t: branch(c, every(q, 'feared')), gain: 0.3 });
      out.push({ name: 'pop', t: c.W('s1_06', 'list'), gain: 0.3 }, { name: 'pop', t: c.W('s1_06', 'fix'), gain: 0.3 });
      return out;
    },
  };

  /* ---- s2 "The case for yes": the weak Articles, THE FEDERALIST by Publius, "If men were angels", the three statements ---- */
  function articles(t, tm) {
    const C = KC(), x = 560, y = 360, w = 640, h = 520, top = y - h / 2, L = x - w / 2 + 70;
    Civic.parchment(x, y, w, h, { seed: 1200, depth: 1.5, color: '#e8d6ae' });
    // tatters: torn notches along the right edge
    [[x + w / 2 - 6, top + 120], [x + w / 2 - 4, top + 330], [x - w / 2 + 8, top + 260]].forEach(([px, py], i) => paperCircle(px, py, 22, '#c9a25a', { seed: 1201 + i, depth: 0.2 }));
    txt('ARTICLES OF', x, top + 64, 40, { font: SERIF, color: INK });
    txt('CONFEDERATION', x, top + 118, 52, { font: SERIF, color: INK });
    TEXT.weak.forEach((l, i) => {
      const t0 = tm.taxes + i * 0.6, yy = top + 250 + i * 80;
      if (t < t0) return;
      hand(l, L + 70, yy, 44, t0, { align: 'left', color: INK, seed: 1210 + i, dur: 0.35 });
      cross(L + 16, yy, 0.62, P(t, t0 + 0.2, t0 + 0.5), 1215 + i * 2);
    });
    stampMark('TOO WEAK', x + 150, top + 200, stampS(t, tm.weak) * 0.62, { rot: 0.14, size: 64 });
    if (t > tm.stronger) tag('a STRONGER national government, with checks', x, top + h + 30, 32, pop(t, tm.stronger, 0.35), { color: KC().navy, ink: KC().cream, seed: 1220, rot: -0.02 });
  }
  function publius(t, tm) {
    const C = KC(), P_ = R().PAPERS, x = 1340, y = 360;
    [[-60, 90, -0.12], [40, 70, 0.08], [-20, 40, -0.04], [30, 10, 0.05]].forEach(([dx, dy, r], i) =>
      prop('newspaper_1787', x + dx, y + dy, 300, { s: pop(t, tm.essays + i * 0.1, 0.35), rot: r, seed: 1230 + i }));
    const s = pop(t, tm.pub, 0.4, 1.6);
    if (s > 0) at(x, y - 150, s, x, y - 150, () => {
      paperRect(x, y - 150, 520, 130, C.navy, { seed: 1240, depth: 1.3 });
      txt('THE FEDERALIST', x, y - 172, 46, { font: SLAB, color: C.cream });
      txt(`by ${P_.penName.toUpperCase()}`, x, y - 120, 30, { font: TYPE, color: C.cream });
    });
    if (t > tm.eighty) {
      const n = Math.round(P_.count * E.outCubic(P(t, tm.eighty, tm.eighty + 0.8)));
      tag(`${n} essays`, x + 170, y + 190, 44, pop(t, tm.eighty, 0.35), { font: SLAB, color: '#fffdf7', ink: C.navy, seed: 1241, rot: 0.04 });
    }
    if (t > tm.york) tag(`written for ${R().name(P_.forState)}`, x - 120, y + 262, 32, pop(t, tm.york, 0.35), { color: '#f2d479', ink: INK, seed: 1242, rot: -0.03 });
  }
  BODY.s2 = function (t, part, ctx, c) {
    const C = KC(), q = part.seg.question, rs = replyStart(c, q);
    RK.bgParch();
    const tm = {
      weak: c.W('s2_01', 'weak'), taxes: c.W('s2_01', 'taxes'), stronger: c.W('s2_02', 'stronger'), essays: c.W('s2_02', 'Three'),
      eighty: c.W('s2_02', 'eightyfive'), york: c.W('s2_02', 'York'), pub: c.W('s2_02', 'Publius'), ang: c.W('s2_03', 'angels'), up: c.L('s2_03') - 0.3,
      pres: c.W('s2_04', 'president'), rs, fStamp: branch(c, every(q, 'stronger')), checks: c.W('s2_05', 'checks'), nobody: c.W('s2_05', 'nobody'),
    };
    const fade = 1 - P(t, rs - 0.1, rs + 0.4);
    alpha(fade, () => {
      const up = E.inOutCubic(P(t, tm.up, tm.up + 0.7));
      at(lerp(560, 480, up), lerp(360, 270, up), lerp(1, 0.66, up) * pop(t, CARD.hold + 0.3, 0.4, 1.6), 560, 360, () => articles(t, tm));
      at(lerp(1340, 1420, up), lerp(360, 280, up), lerp(1, 0.7, up), 1340, 360, () => publius(t, tm));
      // "If men were angels..." (The Federalist No. 51), facts 6.3
      const A = V('f-angels');
      quoteStrip(960, 640, 1240, A.text, A.speaker, tm.ang - 0.3, { color: C.navy, seed: 1250, size: 42, rot: -0.01 });
      if (t > tm.pres) sticky(['especially', 'the president!'], 1710, 560, tm.pres, { w: 280, size: 34, color: '#f4c6c0', seed: 1255, rot: 0.06 });
    });
    if (t > tm.taxes && fade > 0.5) dock('articles', ['The Articles of Confederation were weak:'].concat(TEXT.weak.map(l => '- ' + l)).concat(t > tm.pub ? [`The Federalist: ${R().PAPERS.count} essays by "${R().PAPERS.penName}", written for ${R().name(R().PAPERS.forState)}.`] : []).concat(t > tm.ang ? [`"${V('f-angels').text}" (${V('f-angels').speaker})`] : []));
    // the reply: the three statements; the navy F on the Federalist one, the brick A on the others (the notes)
    const rsS = pop(t, rs + 0.1, 0.4, 1.6);
    const optSize = Math.min(...q.options.map(o => fitSize(o, 980, 40, SLAB)));
    if (rsS > 0) q.options.forEach((opt, i) => {
      const y = 200 + i * 165, right = i === q.correct, s = pop(t, rs + 0.1 + i * 0.15, 0.35, 1.6);
      if (s <= 0) return;
      at(900, y, s, 900, y, () => {
        paperRect(900, y, 1180, 124, right && t > tm.fStamp ? '#fbf3df' : '#fffdf7', { seed: 1260 + i * 3 + (right && t > tm.fStamp ? 1 : 0), depth: 1.2 });
        txt(opt, 400, y, optSize, { font: SLAB, color: INK, align: 'left' });
      });
      if (right) sideBadge('federalist', 330, y, 96, stampS(t, tm.fStamp), -0.1, 1270);
      else sideBadge('anti', 330, y, 96, stampS(t, rs + 0.6 + i * 0.2) * 0.9, 0.08, 1272 + i);
    });
    if (t > tm.fStamp) dock('q2', [`Federalist: "${q.options[q.correct]}"`]);
    // after: "a strong government that checks itself" / "not a strong president who checks nobody" (s2_05)
    if (t > tm.checks) {
      sticky(['a strong government', 'that checks itself'], 620, 700, tm.checks, { w: 380, size: 34, color: '#dfe6f2', ink: C.navy, seed: 1280, rot: -0.04 });
      check(830, 690, 0.8, P(t, tm.checks + 0.4, tm.checks + 0.7), 1283, GREEN);
    }
    if (t > tm.nobody) sticky(['a strong president', 'who checks nobody'], 1260, 700, tm.nobody, { w: 380, size: 34, color: '#f4c6c0', seed: 1285, rot: 0.04, strike: tm.nobody + 0.5 });
    const st = stage(t, part, [[0, 'inset']]);
    cast(t, ctx, st);
  };
  S.s2 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 1.6, after: 2.0 }, gaps: { s2_03: 0.5, s2_04: 0.5 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part);
      out.push({ name: 'paper', t: CARD.hold + 0.3, gain: 0.35 }, { name: 'stamp', t: c.W('s2_01', 'weak'), gain: 0.45 });
      [0, 0.6, 1.2].forEach(d => out.push({ name: 'scribble', t: c.W('s2_01', 'taxes') + d + 0.2, gain: 0.22 }));
      out.push({ name: 'pop', t: c.W('s2_02', 'stronger'), gain: 0.3 }, { name: 'paper', t: c.W('s2_02', 'Three'), gain: 0.35 });
      out.push({ name: 'ding', t: c.W('s2_02', 'eightyfive') + 0.8, gain: 0.25 }, { name: 'thunk', t: c.W('s2_02', 'Publius'), gain: 0.35 });
      out.push({ name: 'paper', t: c.W('s2_03', 'angels') - 0.3, gain: 0.35 }, { name: 'pop', t: c.W('s2_04', 'president'), gain: 0.3 });
      out.push({ name: 'whoosh', t: replyStart(c, q), gain: 0.25 }, { name: 'stamp', t: branch(c, every(q, 'stronger')), gain: 0.5 });
      out.push({ name: 'pop', t: c.W('s2_05', 'checks'), gain: 0.3 }, { name: 'scribble', t: c.W('s2_05', 'nobody') + 0.5, gain: 0.3 });
      return out;
    },
  };

  /* ---- s3 "Yes, and...": Mason, Henry, Hamilton; Massachusetts SPLIT → 187 to 168, YES and nine amendments ---- */
  function crown(x, y, s, seed) {
    if (s <= 0.001) return;
    paperPiece(Object.assign([[-40, 20], [-44, -22], [-20, 0], [0, -30], [20, 0], [44, -22], [40, 20]], { k: 'rt:crown' }), x, y, KC().gold, { seed, depth: 0.8, s });
  }
  BODY.s3 = function (t, part, ctx, c) {
    const C = KC(), q = part.seg.question, rs = replyStart(c, q), ma = R().st('MA');
    RK.bgNews();
    const tm = {
      mason: c.W('s3_01', 'Mason'), there: c.W('s3_01', 'There'), henry: c.W('s3_02', 'your'), personal: c.W('s3_03', 'personal'),
      ham: c.W('s3_04', 'why'), mass: c.W('s3_04', 'Massachusetts'), split: c.W('s3_04', 'split'),
      rs, settle: rs + 0.3, rec: branch(c, every(q, 'recommend')), lists: c.W('s3_05', 'lists'),
    };
    // the three quotes (VIEWS: a-mason 6.6, a-king 6.8, f-why-declare 6.4), gone when Massachusetts comes up
    const qf = 1 - P(t, tm.mass - 0.2, tm.mass + 0.3);
    alpha(qf, () => {
      const M = V('a-mason'), H = V('a-king'), F = V('f-why-declare');
      if (t > tm.mason) txt('ANTI-FEDERALISTS', 180, 96, 34, { font: SLAB, color: C.brick, align: 'left', alpha: P(t, tm.mason, tm.mason + 0.3) });
      quoteStrip(700, 200, 1040, M.text, M.speaker, tm.there - 0.3, { color: C.brick, seed: 1300, size: 42 });
      if (t > tm.there - 0.3 && t < tm.there + 1.5) prop('quill_pen', 460 + 420 * E.inOutSine(P(t, tm.there, tm.there + 1.1)), 160, 150, { s: pop(t, tm.there - 0.3, 0.3) * (1 - P(t, tm.there + 1.2, tm.there + 1.5)), rot: 0.5, seed: 1305 });
      quoteStrip(760, 390, 1040, H.text, H.speaker, tm.henry - 0.2, { color: C.brick, seed: 1310, size: 42, rot: 0.01 });
      quoteStrip(1180, 600, 1100, F.text, F.speaker, tm.ham - 0.2, { color: C.navy, seed: 1320, size: 40, rot: -0.01 });
      if (t > tm.ham) txt('FEDERALIST REPLY', 1700, 480, 30, { font: SLAB, color: C.navy, align: 'right', alpha: P(t, tm.ham, tm.ham + 0.3) });
      if (t > tm.there) dock('quotes', [`"${M.text}" (${M.speaker})`].concat(t > tm.henry ? [`"${H.text}" (${H.speaker})`] : []).concat(t > tm.ham ? [`"${F.text}" (${F.speaker})`] : []));
    });
    // "That's a little personal" (s3_03): a paper crown over Rex, struck
    if (t > tm.personal && t < tm.mass) {
      const s = pop(t, tm.personal, 0.35) * (1 - P(t, tm.mass - 0.5, tm.mass - 0.2));
      crown(200, 740, s, 1330);
      if (t > tm.personal + 0.5) cross(200, 740, 0.9 * s, P(t, tm.personal + 0.5, tm.personal + 0.8), 1331);
    }
    // Massachusetts on the ledger (facts 2.16): SPLIT, swaying; settles to the real vote in the reply
    if (t > tm.mass) {
      const ms = pop(t, tm.mass, 0.4, 1.6), real = ma.yes / (ma.yes + ma.no);
      const settle = E.inOutCubic(P(t, tm.settle, tm.settle + 0.9));
      const swayShare = 0.5 + 0.045 * Math.sin(t * 3.3);
      const done = settle >= 1;
      at(960, 360, 1.2 * ms, 960, 360, () => {
        Civic.parchment(960, 360, 1100, 150, { seed: 1340, depth: 1.4 });
        txt('THE LEDGER', 430, 305, 22, { font: TYPE, color: PENCIL, align: 'left' });
        ledgerRow(ma, 960 - 530, 372, 1060, 60, { name: -1e6, month: tm.mass + 0.2, bar: done ? tm.settle : null, sway: done ? null : lerp(swayShare, real, settle) });
      });
      stampMark('SPLIT', 1450, 250, stampS(t, tm.split) * 0.8 * (1 - P(t, tm.settle, tm.settle + 0.3)), { rot: 0.12, size: 64 });
      if (t > tm.settle + 1.1) tag(`yes by ${R().margin('MA')}`, 1400, 470, 36, pop(t, tm.settle + 1.1, 0.35), { color: '#f2d479', ink: C.navy, seed: 1341, rot: 0.03 });
      if (t > tm.mass) dock('ma', [`${ma.name}, ${ma.shown}: ` + (done ? `${vote(ma.yes, ma.no)}, yes by ${R().margin('MA')}.` : 'split almost down the middle.')]
        .concat(t > tm.rec ? [`YES, and please add these ${numWord(ma.amendments)} amendments.`] : []));
    }
    // the reply: the scroll "YES, and please add these nine amendments" (facts 5.1)
    const un = E.outCubic(P(t, tm.rec, tm.rec + 0.7));
    if (un > 0) scroll(960, 640, 1000, 220, un, () => {
      Civic.woodtype('YES, AND...', 960, 580, 40, { color: C.navy, ink: C.cream, t0: tm.rec + 0.2, seed: 1350 });
      const l = `please add these ${numWord(ma.amendments)} amendments`;
      hand(l, 960, 660, fitSize(l, 900, 48), tm.rec + 0.5, { color: INK, seed: 1351, dur: 0.6 });
    }, 1352);
    // after (s3_05): the states that sent lists of amendments (Ratify.recommendedStates)
    if (t > tm.lists) {
      const rec = R().recommendedStates();
      txt('sent lists of amendments:', 460, 150, 32, { color: INK, align: 'right', alpha: P(t, tm.lists, tm.lists + 0.3) });
      rec.forEach((code, i) => tag(code, 530 + i * 118, 150, 38, pop(t, tm.lists + i * 0.12, 0.3), { font: SLAB, color: '#fffdf7', ink: C.brick, seed: 1360 + i, rot: (i % 2 ? 0.05 : -0.05) }));
    }
    const st = stage(t, part, [[0, 'inset']]);
    cast(t, ctx, st);
  };
  S.s3 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.8, reply: 1.8, after: 2.0 }, gaps: { s3_02: 0.5, s3_04: 0.5 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part);
      out.push({ name: 'paper', t: c.W('s3_01', 'There') - 0.3, gain: 0.35 }, { name: fx('quill_scratch', 'scribble'), t: c.W('s3_01', 'There'), gain: 0.3 });
      out.push({ name: 'paper', t: c.W('s3_02', 'your') - 0.2, gain: 0.35 }, { name: 'boing', t: c.W('s3_03', 'personal'), gain: 0.25 });
      out.push({ name: 'paper', t: c.W('s3_04', 'why') - 0.2, gain: 0.35 }, { name: fx('crowd_vote', 'whoosh'), t: c.W('s3_04', 'Massachusetts'), gain: 0.35 });
      out.push({ name: 'stamp', t: c.W('s3_04', 'split'), gain: 0.45 }, { name: 'thunk', t: replyStart(c, q) + 1.2, gain: 0.35 });
      out.push({ name: fx('unroll', 'paper'), t: branch(c, every(q, 'recommend')), gain: 0.45 }, { name: 'pop', t: c.W('s3_05', 'lists'), gain: 0.3 });
      return out;
    },
  };

  /* ---- s4 "Road to nine": the ledger fills (Ratify.STATES), the counter and the bell, NC NOT YET, New York 30–27 ---- */
  const LG = { x: 690, y: 425, w: 1180, h: 720 };
  /* each state's reveal time (name, month, bar) from the dialogue */
  function rowTimes(c) {
    const Rt = R(), T_ = {}, de = c.W('s4_01', 'Delaware'), nh = c.W('s4_01', 'ninth'), mid0 = c.W('s4_01', 'thirty') + 0.5, mid1 = c.W('s4_01', 'Hampshire') - 0.2;
    const ninthCode = Rt.ninth(), k9 = Rt.order(ninthCode);
    Rt.STATES.forEach(st => {
      let t0;
      if (st.order === 1) t0 = de;
      else if (st.order < k9) t0 = lerp(mid0, mid1, (st.order - 2) / Math.max(1, k9 - 3));
      else if (st.code === ninthCode) t0 = nh;
      else t0 = null;
      if (t0 != null) T_[st.code] = { name: t0, month: t0 + 0.1, bar: t0 + 0.2 };
    });
    T_.VA = { name: c.W('s4_02', 'Virginia'), month: c.W('s4_02', 'Virginia') + 0.1, bar: c.W('s4_02', 'Virginia') + 0.2 };
    const nc = c.W('s4_02', 'North'), way = c.W('s4_02', 'way');
    T_.NC = { name: nc, month: way + 0.3, bar: way + 0.4, notYet: nc + 0.2 };
    T_.RI = { name: c.W('s4_02', 'Rhode'), month: c.W('s4_02', 'Rhode') + 0.1, bar: c.W('s4_02', 'two') };
    T_.NY = { name: c.W('s4_03', 'York'), month: c.W('s4_03', 'York') + 0.1, bar: c.W('s4_04', 'Thirty') };
    return T_;
  }
  /* the New York card: the tally piles (yes navy, no brick); k0 / k1 = the slide times of the switching marks */
  const NYC = { x: 960, y: 420, w: 1080, h: 450 };
  function nyCard(t, slides) {
    const C = KC(), ny = R().st('NY'), top = NYC.y - NYC.h / 2;
    Civic.parchment(NYC.x, NYC.y, NYC.w, NYC.h, { seed: 1400, depth: 1.6 });
    txt(ny.name.toUpperCase(), NYC.x - NYC.w / 2 + 50, top + 56, 48, { font: SLAB, color: INK, align: 'left' });
    txt(ny.shown, NYC.x + NYC.w / 2 - 50, top + 60, 34, { font: TYPE, color: PENCIL, align: 'right' });
    const yx = NYC.x - NYC.w / 2 + 70, nx = NYC.x + 70, py = top + 190;
    // how many have switched by now (each slide 0..1)
    const u = slides.map(t0 => E.inOutCubic(P(t, t0, t0 + 0.7)));
    const k = u.filter(v => v >= 1).length, cur = R().afterFlip('NY', k);
    txt(`YES ${cur.yes}`, yx, top + 124, 42, { font: SLAB, color: C.navy, align: 'left' });
    txt(`NO ${cur.no}`, nx, top + 124, 42, { font: SLAB, color: C.brick, align: 'left' });
    ink([[NYC.x + 20, top + 100], [NYC.x + 20, top + NYC.h - 40]], { w: 2.5, color: 'rgba(43,36,32,0.5)', seed: 1401, passes: 1, step: 60 });
    const moving = slides.length;
    for (let i = 0; i < ny.yes; i++) {
      const j = ny.yes - 1 - i; // j-th from the end
      if (j < moving) {
        const e = u[j], [ax, ay] = tallyPos(yx, py, i), [bx, by] = tallyPos(nx, py, ny.no + j);
        const col = e > 0.5 ? C.brick : C.navy;
        tallyMark(lerp(ax, bx, e), lerp(ay, by, e) - Math.sin(e * Math.PI) * 60, col, 1410 + i);
      } else { const [ax, ay] = tallyPos(yx, py, i); tallyMark(ax, ay, C.navy, 1410 + i); }
    }
    for (let i = 0; i < ny.no; i++) { const [bx, by] = tallyPos(nx, py, i); tallyMark(bx, by, C.brick, 1460 + i); }
    // who wins right now
    const winner = cur.yes > cur.no ? ['YES WINS', C.navy] : ['NO WINS', C.brick];
    if (slides.length && t > slides[0]) tag(winner[0], NYC.x, top + NYC.h - 50, 36, 1, { font: SLAB, color: winner[1], ink: C.cream, seed: winner[0] === 'YES WINS' ? 1490 : 1491, rot: -0.02 });
  }
  /* the q4 working cues (per branch: the word each step lands on; '@' = the line's start) */
  const STEPCUE = {
    q4_right: ['@', 'one', ['two', 2]],
    q4_wrong0: ['@', 'each', 'Two'],
    q4_wrong1: ['@', 'Just', 'make'],
  };
  function stepTimes(c, q) {
    const cardIn = replyStart(c, q) + 0.5, out = []; let prev = cardIn;
    for (let i = 0; i < q.working.steps.length; i++) {
      const tbl = {}; for (const id in STEPCUE) tbl[id] = STEPCUE[id][i];
      const cue = branch(c, tbl), t0 = Math.max(cue, prev + (i ? 0.8 : 0.2));
      out.push(t0); prev = t0;
    }
    return { cardIn, steps: out };
  }
  BODY.s4 = function (t, part, ctx, c) {
    const C = KC(), q = part.seg.question, rs = replyStart(c, q), Rt = R();
    RK.bgDesk();
    const rt = rowTimes(c);
    const tm = { rs, zoom: c.E('s4_04') + 0.1, two: c.W('s4_05', 'Two'), back: c.L('s4_06') - 0.3, three: c.W('s4_06', 'three') };
    const nineT = rt[Rt.ninth()].bar;
    // the counter: rows whose bar has filled (every row is a Ratify.STATES ratification)
    const n = Rt.STATES.filter(st => rt[st.code] && t >= rt[st.code].bar).length;
    RV.counter = n; RV.rows = Rt.STATES.map(st => [st.code, st.shown, st.yes, st.no]);
    const nyZoom = t > tm.back ? 1 - E.inOutCubic(P(t, tm.back, tm.back + 0.6)) : E.inOutCubic(P(t, tm.zoom, tm.zoom + 0.6));
    const unan = new Set(Rt.unanimousStates());
    const lg = ledger(LG.x, LG.y, LG.w, LG.h, st => {
      const r = rt[st.code] || {};
      return { name: r.name, month: r.month, bar: r.bar, hi: (st.code === 'NY' && t > rt.NY.name ? 1 : 0) + (unan.has(st.code) && t > tm.three ? P(t, tm.three, tm.three + 0.3) : 0) };
    });
    const rowY = code => lg.ys[Rt.order(code) - 1];
    // the stamps and notes on the rows
    const first = rt[Rt.nth(1)];
    if (t > first.bar) tag('first', LG.x + LG.w / 2 - 52, rowY(Rt.nth(1)), 24, pop(t, first.bar + 0.3, 0.3), { font: SLAB, color: '#f2d479', ink: INK, seed: 1500, rot: 0.06 });
    if (t > nineT) tag('ninth!', LG.x + LG.w / 2 - 52, rowY(Rt.ninth()), 24, pop(t, nineT + 0.2, 0.3), { font: SLAB, color: C.navy, ink: C.cream, seed: 1501, rot: -0.05 });
    const ncT = rt.NC;
    stampMark(`NOT YET (${Rt.NC.refusedYear})`, lg.bx + lg.bw / 2, rowY('NC'), stampS(t, ncT.notYet) * 0.46 * (1 - P(t, ncT.month - 0.3, ncT.month)), { rot: -0.04, size: 64 });
    if (t > ncT.bar) tag('yes', LG.x + LG.w / 2 - 52, rowY('NC'), 24, pop(t, ncT.bar + 0.4, 0.3), { font: SLAB, color: '#fffdf7', ink: GREEN, seed: 1502, rot: 0.04 });
    if (t > rt.RI.bar) tag('last', LG.x + LG.w / 2 - 52, rowY('RI'), 24, pop(t, rt.RI.bar + 0.3, 0.3), { font: SLAB, color: '#f2d479', ink: INK, seed: 1503, rot: -0.04 });
    // after (s4_06): the unanimous rows ringed; "only three"
    if (t > tm.three) {
      unan.forEach(code => inkCircle(lg.left + 975, rowY(code), 64, { sx: 1.0, sy: 0.4, w: 4, color: GREEN, seed: 1510 + Rt.order(code), prog: P(t, tm.three, tm.three + 0.4) }));
      tag(`only ${numWord(Rt.unanimousCount())} unanimous`, 1560, 740, 34, pop(t, tm.three + 0.3, 0.35), { color: '#fffdf7', ink: GREEN, seed: 1515, rot: 0.03 });
    }
    // the counter and New Hampshire's bell (Ratify.ninth())
    counter(1560, 210, n, pop(t, CARD.hold + 0.4, 0.4, 1.6), { pulse: t > nineT ? P(t, nineT, nineT + 0.4) : 0 });
    const ring = t > nineT ? Math.sin((t - nineT) * 11) * 0.35 * Math.exp(-(t - nineT) * 1.2) : 0;
    prop('bell', 1560, 500, 230, { s: pop(t, CARD.hold + 0.6, 0.4), seed: 1520, swing: ring });
    if (t > nineT) tag(`${Rt.name(Rt.ninth())} makes ${numWord(Rt.article7)}`, 1560, 660, 32, pop(t, nineT + 0.3, 0.35), { color: '#f2d479', ink: C.navy, seed: 1521, rot: -0.03 });
    // dock: the counter + the latest row
    const shownRows = Rt.STATES.filter(st => rt[st.code] && t >= rt[st.code].bar).sort((a, b) => rt[a.code].bar - rt[b.code].bar);
    const last = shownRows[shownRows.length - 1];
    if (nyZoom < 0.5 && t > CARD.hold + 1) dock('ledger', [`RATIFIED: ${n} of ${Rt.article7} needed.`].concat(last ? [`${last.name}, ${last.shown}: ${vote(last.yes, last.no)}.`] : [])
      .concat(t > ncT.notYet && t < ncT.bar ? [`North Carolina: not yet (${Rt.NC.refusedYear}).`] : []).concat(t > tm.three ? [`Unanimous: ${Rt.unanimousNames()}.`] : []));
    // the New York card: enlarges for the question; the working + the two switching marks in the reply
    if (nyZoom > 0.001) {
      g.save(); g.globalAlpha *= 0.45 * nyZoom; g.fillStyle = '#2b2420'; g.fillRect(0, 0, 1920, 1080); g.restore();
      const ny = rowY('NY'), wt = stepTimes(c, q), left = E.inOutCubic(P(t, rs, rs + 0.7));
      const slides = [wt.steps[1], wt.steps[2]].slice(0, Rt.flip('NY'));
      const cx = lerp(NYC.x, 560, left), cy = lerp(NYC.y, 420, left), cs = lerp(1, 0.78, left);
      at(lerp(LG.x, cx, nyZoom), lerp(ny, cy, nyZoom), lerp(0.25, cs, nyZoom), NYC.x, NYC.y, () => nyCard(t, t > rs - 0.1 ? slides : []));
      const steps = workCard(q, 1400, 420, 720, wt, wt.steps[2] + 1.0, { answer: 'Answer: ' + q.options[q.correct], head: 'New York' });
      if (t > wt.cardIn && steps) {
        const shown = wt.steps.filter(t0 => t >= t0 + 0.2).length;
        RV.steps = { shown, n: steps.length, values: steps.map(s => s.value) };
        dock('q4work', ['The working:'].concat(steps.slice(0, shown).map(s => `${s.n}. ${s.text} ${s.value}`)));
      } else if (nyZoom > 0.5) { const nyS = Rt.st('NY'); dock('ny', [`${nyS.name}, ${nyS.shown}: ${nyS.yes} yes, ${nyS.no} no.`]); }
      if (t > tm.two) tag(`${Rt.flip('NY')} switches`, 560, 760, 38, pop(t, tm.two, 0.35), { font: SLAB, color: '#f2d479', ink: INK, seed: 1530, rot: -0.03 });
    }
    const st = stage(t, part, [[0, 'inset']]);
    cast(t, ctx, st);
  };
  S.s4 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.6, reply: 2.2, after: 2.0 }, gaps: { s4_02: 0.6, s4_03: 0.5, s4_04: 0.4 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part), rt = rowTimes(c), Rt = R();
      Rt.STATES.forEach(st => { if (rt[st.code]) out.push({ name: 'scribble', t: rt[st.code].bar, gain: 0.16 }); });
      const nineT = rt[Rt.ninth()].bar;
      out.push({ name: fx('bell_ring', 'ding'), t: nineT, gain: 0.5 }, { name: fx('cheer', 'sparkle'), t: nineT + 0.3, gain: 0.35 });
      out.push({ name: 'stamp', t: rt.NC.notYet, gain: 0.4 }, { name: 'whoosh', t: c.E('s4_04') + 0.1, gain: 0.3 });
      const wt = stepTimes(c, q);
      out.push({ name: 'paper', t: wt.cardIn, gain: 0.3 });
      wt.steps.forEach(t0 => out.push({ name: 'scribble', t: t0 + 0.1, gain: 0.22 }));
      out.push({ name: 'boing', t: wt.steps[1] + 0.3, gain: 0.2 }, { name: 'boing', t: wt.steps[2] + 0.3, gain: 0.2 });
      out.push({ name: 'pop', t: c.W('s4_05', 'Two'), gain: 0.3 }, { name: 'whoosh', t: c.L('s4_06') - 0.3, gain: 0.25 }, { name: 'scribble', t: c.W('s4_06', 'three'), gain: 0.25 });
      return out;
    },
  };

  /* ---- s5 "Write it down": Madison proposes; 17 → 12 → 10; the 27th in 1992; F + A → one book; STUDENT RIGHTS; AMENDED ---- */
  function funnel(t, tm) {
    const C = KC(), b = R().bor;
    const rows = [[`HOUSE: ${b.house}`, b.house, tm.house], [`SENATE: ${b.senate}`, b.senate, tm.senate], [`STATES RATIFY: ${b.ratified}`, b.ratified, tm.ten]];
    rows.forEach(([label, v, t0], i) => {
      const y = 300 + i * 125, w = Math.round(900 * v / b.house), s = pop(t, t0, 0.35, 1.6);
      if (s <= 0) return;
      at(960, y, s, 960, y, () => {
        paperRect(960, y, w, 92, i === 2 ? '#fbf3df' : C.cream, { seed: 1600 + i, depth: 1.2 });
        txt(label, 960, y + 2, fitSize(label, w - 40, 44, SLAB), { font: SLAB, color: i === 2 ? C.navy : INK });
      });
      if (i > 0) inkArrow(960, y - 118 + 46, 960, y - 48, { w: 5, color: C.brick, seed: 1605 + i, prog: P(t, t0 - 0.1, t0 + 0.2), head: 16 });
    });
    const bs = pop(t, tm.bill, 0.4, 1.6);
    if (bs > 0) {
      inkArrow(960, 300 + 2 * 125 + 46, 960, 640, { w: 5, color: C.navy, seed: 1609, prog: P(t, tm.bill - 0.1, tm.bill + 0.2), head: 16 });
      at(960, 690, bs, 960, 690, () => {
        paperRect(960, 690, 1000, 96, C.navy, { seed: 1610, depth: 1.3 });
        const l = `${b.shown}: THE BILL OF RIGHTS`;
        txt(l, 960, 692, fitSize(l, 940, 50, SLAB), { font: SLAB, color: C.cream });
      });
    }
  }
  function book(x, y, s, t0) {
    if (s <= 0.001) return;
    const C = KC();
    g.save(); g.translate(x, y); g.rotate(-0.03); g.scale(s, s);
    paperRect(-150, 0, 290, 380, C.navy, { seed: 1620, depth: 1.5 });
    paperRect(150, 0, 290, 380, C.brick, { seed: 1621, depth: 1.5 });
    paperRect(0, 0, 34, 392, C.gold, { seed: 1622, depth: 1 });
    txt('F', -150, -60, 110, { font: SLAB, color: C.cream });
    txt('A', 150, -60, 110, { font: SLAB, color: C.cream });
    paperRect(0, 110, 440, 84, C.cream, { seed: 1623, depth: 0.8 });
    txt('THE BILL OF RIGHTS', 0, 112, 36, { font: SLAB, color: INK });
    g.restore();
  }
  BODY.s5 = function (t, part, ctx, c) {
    const C = KC(), b = R().bor;
    RK.bgParch();
    const tm = {
      mad: c.W('s5_01', 'Madison'), prop: c.W('s5_01', 'proposed'), house: c.W('s5_02', 'House'), senate: c.W('s5_02', 'Senate'),
      ten: c.W('s5_02', 'ten'), bill: c.W('s5_02', 'Bill'), left: c.W('s5_03', 'leftovers'), anti: c.W('s5_03', 'AntiFederalists'), written: c.W('s5_03', 'written'),
      ch: c.L('s5_04') - 0.3, section: c.W('s5_04', 'section'), student: c.W('s5_04', 'Student'), judge: c.W('s5_04', 'judge'), amended: c.L('s5_05'),
    };
    const gone = E.inOutCubic(P(t, tm.written, tm.written + 0.6));
    const away = E.inOutCubic(P(t, tm.ch, tm.ch + 0.7));
    alpha(1 - gone, () => {
      const ps = pop(t, tm.prop, 0.4, 1.6);
      if (ps > 0) at(960, 150, ps, 960, 150, () => {
        paperRect(960, 150, 980, 110, C.navy, { seed: 1630, depth: 1.3 });
        const l = `${b.proposed}: Madison proposes amendments`;
        txt(l, 960, 152, fitSize(l, 920, 46, SLAB), { font: SLAB, color: C.cream });
      });
      funnel(t, tm);
      if (t > tm.left) sticky([`${numWord(b.sent - b.ratified - b.neverRatified)} leftover: the ${b.art27num}th`, `Amendment, ${b.art27}`], 1640, 640, tm.left, { w: 320, size: 32, seed: 1640, rot: 0.05 });
    });
    if (t > tm.prop && gone < 0.5) dock('bor', [`${b.proposed}: Madison proposes amendments.`].concat(t > tm.house ? [`House: ${b.house}. Senate: ${b.senate}. States ratify: ${b.ratified}.`] : [])
      .concat(t > tm.bill ? [`${b.shown}: the Bill of Rights.`] : []).concat(t > tm.left ? [`A leftover became the ${b.art27num}th Amendment in ${b.art27}.`] : []));
    // the two woodtype blocks slide in (the Anti-Federalists lost the vote...) and together into one book (written down)
    if (t > tm.anti && away < 1) alpha(1 - away, () => {
      const m = E.inOutCubic(P(t, tm.written, tm.written + 0.7));
      if (m < 1) {
        sideBadge('federalist', lerp(260, 810, m), lerp(420, 440, m), 150, pop(t, tm.anti, 0.35), -0.08, 1650);
        sideBadge('anti', lerp(1660, 1110, m), lerp(420, 440, m), 150, pop(t, tm.anti + 0.15, 0.35), 0.08, 1651);
      }
      book(960, 440, pop(t, tm.written + 0.6, 0.4, 1.8), tm.written + 0.6);
    });
    // Rex's broadside: STUDENT RIGHTS (s5_04), AMENDED (s5_05)
    const bs = pop(t, tm.ch + 0.3, 0.45, 1.6);
    if (bs > 0) {
      broadside(960, 560, bs, t, { rightsT: tm.section, lineT: tm.student, strikeT: tm.judge, rot: -0.015 });
      if (t > tm.student - 0.2 && t < tm.student + 1.6) prop('quill_pen', 760 + 300 * E.inOutSine(P(t, tm.student, tm.student + 1.1)), 330, 170, { s: pop(t, tm.student - 0.2, 0.3) * (1 - P(t, tm.student + 1.3, tm.student + 1.6)), rot: 0.5, seed: 1660 });
      stampMark('AMENDED', 1370, 380, stampS(t, tm.amended) * 0.95, { rot: -0.16, size: 84 });
      Civic.seal(1150, 800, 56, { text: 'SC', seed: 1661, s: pop(t, tm.amended + 0.35, 0.35, 2.4) });
      if (t > tm.section) dock('rights', ['STUDENT RIGHTS', "1. The president can't judge his own case."].concat(t > tm.amended ? ['AMENDED.'] : []));
    }
    const st = stage(t, part, [[0, 'inset'], [tm.ch, 'two']]);
    cast(t, ctx, st);
  };
  S.s5 = {
    timing: { lead: { lines: CARD.lead }, hold: { lines: 2.6 }, gaps: { s5_02: 0.5, s5_04: 0.9, s5_05: 0.4 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: 'paper', t: c.W('s5_01', 'proposed'), gain: 0.35 });
      ['House', 'Senate', 'ten'].forEach(w => out.push({ name: 'pop', t: c.W('s5_02', w), gain: 0.3 }));
      out.push({ name: 'ding', t: c.W('s5_02', 'Bill'), gain: 0.3 }, { name: 'pop', t: c.W('s5_03', 'leftovers'), gain: 0.3 });
      out.push({ name: 'thunk', t: c.W('s5_03', 'AntiFederalists'), gain: 0.35 }, { name: 'thunk', t: c.W('s5_03', 'written') + 0.6, gain: 0.4 });
      out.push({ name: 'whoosh', t: c.L('s5_04') - 0.3, gain: 0.25 }, { name: 'paper', t: c.W('s5_04', 'section'), gain: 0.35 });
      out.push({ name: fx('quill_scratch', 'scribble'), t: c.W('s5_04', 'Student'), gain: 0.3 }, { name: 'scribble', t: c.W('s5_04', 'judge'), gain: 0.3 });
      const a = c.L('s5_05');
      out.push({ name: 'stamp', t: a, gain: 0.6 }, { name: 'pop', t: a + 0.35, gain: 0.35 }, { name: fx('cheer', 'sparkle'), t: c.E('s5_05') + 0.2, gain: 0.45 });
      return out;
    },
  };

  /* wrap every scene: the chapter card that tears away to the scene; sfx outside the part are dropped */
  for (const id of Object.keys(BODY)) {
    S[id].draw = function (t, part, ctx) {
      const c = cues(ctx), i = segIndex(part.seg);
      if (part.kind === 'lines' && t < CARD.hold + CARD.tear) {
        if (t < CARD.hold) drawCard(i);
        else tornTransition(() => drawCard(i), u => BODY[id](u, part, ctx, c), t, CARD.hold, CARD.tear, 40 + i);
      } else BODY[id](t, part, ctx, c);
    };
    const sfx = S[id].sfx;
    S[id].sfx = (part, ctx) => sfx(part, ctx).filter(e => e && e.name && e.t > -1e5 && e.t < 1e5);
  }

  window.RatifyVideo = Object.assign(RV, { BODY, TEXT, layers: RK.LY });
  Object.defineProperty(RV, 'lastDock', { get: () => RK.K.lastDock, enumerable: true });

  class VideoScene extends Player.Scene {
    enter() {
      super.enter();
      if (typeof prewarm === 'function') [RK.bgShop, RK.bgParch, RK.bgNews, RK.bgDesk].forEach(f => prewarm.draw(() => f()));
    }
  }
  Levels.video = VideoScene;
})();
